import { useState, useEffect, useCallback } from 'react';
import { logger } from '@/utils/Logger';
import audioLanguageJson from '@/assets/audioLanguages.json';
import useGetGenresQuery from './useGetGenresQuery';

export interface SelectOption {
  label: string;
  value: any;
}

/**
 * Custom hook to fetch and manage master catalog data such as genres and audio languages.
 * Provides state variables and filter functions for dropdown/select components.
 *
 * @returns An object containing:
 * - `genres`: Array of fetched genres.
 * - `audioLanguages`: Array of mapped audio languages.
 * - `queryGenres`: Function to filter genres based on text input.
 * - `queryAudioLanguages`: Function to filter audio languages based on text input.
 */
export const useCatalogData = () => {
  const { data: genres } = useGetGenresQuery();
  const [audioLanguages, setAudioLanguages] = useState<SelectOption[]>([]);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState<boolean>(true);

  const getAudioLanguages = useCallback(async () => {
    try {
      const formattedLanguages = audioLanguageJson.map((ml) => ({
        label: ml.DisplayName,
        value: ml.Reference,
      }));
      setAudioLanguages(formattedLanguages);
    } catch (error: any) {
      logger.error(
        `Error formatting audio languages: ${error?.message || error}`
      );
    }
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoadingCatalogs(true);
      await Promise.all([getAudioLanguages()]);
      setIsLoadingCatalogs(false);
    };
    void fetchAllData();
  }, [getAudioLanguages]);

  const queryGenres = useCallback(
    (inputText: string | null, callback: any) => {
      if (!inputText) {
        callback(genres);
      } else {
        const lowercaseInput = inputText.toLowerCase();
        callback(
          genres?.filter((g) => g.label.toLowerCase().includes(lowercaseInput))
        );
      }
    },
    [genres]
  );

  const queryAudioLanguages = useCallback(
    (inputText: string | null, callback: any) => {
      if (!inputText) {
        callback(audioLanguages);
      } else {
        const lowercaseInput = inputText.toLowerCase();
        callback(
          audioLanguages.filter((ml) =>
            ml.label.toLowerCase().includes(lowercaseInput)
          )
        );
      }
    },
    [audioLanguages]
  );

  return {
    genres,
    audioLanguages,
    queryGenres,
    queryAudioLanguages,
    isLoadingCatalogs,
  };
};
